import { useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";
import { useNotificationContext } from "cerulean";

import { DATE_PRESETS, MAX_VIEW_NAME_LENGTH } from "../constants";

import type { DatePreset } from "../constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Raw shape returned by the API for a single saved view */
interface ApiAdvancedExportSavedView {
  id: string;
  name: string;
  is_preset: boolean;
  filters: {
    account_ids: string[];
    date_range:
      | { preset: string }
      | { preset: "custom"; start_date: string; end_date: string };
    type_codes: string[];
  };
}

/** Normalized shape used by the frontend */
export interface AdvancedExportSavedView {
  id: string;
  name: string;
  view_type: "narmi_preset" | "user";
  filters: {
    account_ids: string[];
    date_range:
      | { preset: string }
      | { preset: "custom"; start_date: string; end_date: string };
    type_codes: string[];
  };
}

/** API list response envelope */
interface AdvancedExportSavedViewListResponse {
  advanced_export_saved_views: ApiAdvancedExportSavedView[];
}

/** Deserialize API response to frontend shape */
function deserializeAdvancedExportSavedView(
  raw: ApiAdvancedExportSavedView
): AdvancedExportSavedView {
  return {
    id: raw.id,
    name: raw.name,
    view_type: raw.is_preset ? "narmi_preset" : "user",
    filters: raw.filters,
  };
}

/** Frontend filter state returned by selectView */
export interface FilterState {
  selectedAccountIds: string[];
  selectedPreset: DatePreset | null;
  startDate: string;
  endDate: string;
  selectedTypeCodes: string[];
}

export interface UseSavedViewsOptions {
  currentFilters: {
    selectedAccountIds: string[];
    selectedPreset: DatePreset | null;
    startDate: string;
    endDate: string;
    selectedTypeCodes: string[];
  };
  validAccountIds: string[];
}

export interface UseSavedViewsResult {
  views: AdvancedExportSavedView[];
  isLoading: boolean;
  selectedView: AdvancedExportSavedView | null;
  selectView: (viewId: string) => FilterState;
  clearSelection: () => void;
  isModified: boolean;
  isPresetView: boolean;
  saveNewView: (name: string) => Promise<boolean>;
  updateView: () => Promise<void>;
  validateName: (name: string) => string | null;
}

// ---------------------------------------------------------------------------
// Helpers — filter serialization / deserialization
// ---------------------------------------------------------------------------

/**
 * Deserialize API filter format → frontend FilterState.
 * Stale account IDs (not in validAccountIds) are silently excluded.
 */
function deserializeFilters(
  apiFilters: AdvancedExportSavedView["filters"],
  validAccountIds: string[]
): FilterState {
  const validSet = new Set(validAccountIds);
  const selectedAccountIds = apiFilters.account_ids.filter((id) =>
    validSet.has(id)
  );

  let selectedPreset: DatePreset | null = null;
  let startDate = "";
  let endDate = "";

  if (apiFilters.date_range.preset === "custom") {
    const range = apiFilters.date_range as {
      preset: "custom";
      start_date: string;
      end_date: string;
    };
    startDate = range.start_date;
    endDate = range.end_date;
  } else {
    selectedPreset =
      DATE_PRESETS.find((p) => p.value === apiFilters.date_range.preset) ??
      null;
    if (selectedPreset) {
      startDate = selectedPreset.minDate();
      endDate = selectedPreset.maxDate();
    }
  }

  return {
    selectedAccountIds,
    selectedPreset,
    startDate,
    endDate,
    selectedTypeCodes: apiFilters.type_codes,
  };
}

/**
 * Serialize frontend filter state → API filter format.
 */
function serializeFilters(filters: {
  selectedAccountIds: string[];
  selectedPreset: DatePreset | null;
  startDate: string;
  endDate: string;
  selectedTypeCodes: string[];
}): AdvancedExportSavedView["filters"] {
  let date_range: AdvancedExportSavedView["filters"]["date_range"];

  if (filters.selectedPreset) {
    date_range = {
      preset: filters.selectedPreset.value,
    };
  } else if (filters.startDate && filters.endDate) {
    date_range = {
      preset: "custom",
      start_date: filters.startDate,
      end_date: filters.endDate,
    };
  } else {
    date_range = {
      preset: "previous_month",
    };
  }

  return {
    account_ids: filters.selectedAccountIds,
    date_range,
    type_codes: filters.selectedTypeCodes,
  };
}

/**
 * Deep equality check for comparing filter state (by value, not reference).
 * Compares the serialized API format of both filter states.
 */
function filtersAreEqual(
  a: AdvancedExportSavedView["filters"],
  b: AdvancedExportSavedView["filters"]
): boolean {
  // Compare account_ids (order-insensitive)
  const aAccounts = [...a.account_ids].sort();
  const bAccounts = [...b.account_ids].sort();
  if (aAccounts.length !== bAccounts.length) return false;
  if (aAccounts.some((id, i) => id !== bAccounts[i])) return false;

  // Compare type_codes (order-insensitive)
  const aCodes = [...a.type_codes].sort();
  const bCodes = [...b.type_codes].sort();
  if (aCodes.length !== bCodes.length) return false;
  if (aCodes.some((code, i) => code !== bCodes[i])) return false;

  // Compare date_range
  if (a.date_range.preset !== b.date_range.preset) return false;
  if (a.date_range.preset === "custom" && b.date_range.preset === "custom") {
    const aRange = a.date_range as {
      start_date: string;
      end_date: string;
    };
    const bRange = b.date_range as {
      start_date: string;
      end_date: string;
    };
    if (aRange.start_date !== bRange.start_date) return false;
    if (aRange.end_date !== bRange.end_date) return false;
  }

  return true;
}

export const useSavedViews = ({
  currentFilters,
  validAccountIds,
}: UseSavedViewsOptions): UseSavedViewsResult => {
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();

  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);

  const { data: views = [], isLoading } = useQuery<AdvancedExportSavedView[]>({
    queryKey: ["savedViews"],
    queryFn: async () => {
      const response = await ApiHttp.fetch<AdvancedExportSavedViewListResponse>(
        "reports/saved_views/"
      );
      return (response.advanced_export_saved_views ?? []).map(
        deserializeAdvancedExportSavedView
      );
    },
  });

  const selectedView = views.find((v) => v.id === selectedViewId) ?? null;
  const isPresetView = selectedView?.view_type === "narmi_preset";

  const isModified = useMemo(() => {
    if (!selectedView) return false;
    const currentSerialized = serializeFilters(currentFilters);
    // Compare against the view's filters with stale account IDs removed,
    // matching the FilterState that selectView() returns to the caller.
    const validSet = new Set(validAccountIds);
    const viewFiltersNormalized: AdvancedExportSavedView["filters"] = {
      ...selectedView.filters,
      account_ids: selectedView.filters.account_ids.filter((id) =>
        validSet.has(id)
      ),
    };
    return !filtersAreEqual(currentSerialized, viewFiltersNormalized);
  }, [selectedView, currentFilters, validAccountIds]);

  const selectView = (viewId: string): FilterState => {
    setSelectedViewId(viewId);
    const view = views.find((v) => v.id === viewId);
    if (!view) {
      throw new Error(`View not found: ${viewId}`);
    }
    return deserializeFilters(view.filters, validAccountIds);
  };

  const clearSelection = () => {
    setSelectedViewId(null);
  };

  const saveNewView = async (name: string): Promise<boolean> => {
    try {
      const filters = serializeFilters(currentFilters);
      await ApiHttp.fetch(
        "reports/saved_views/",
        { method: "POST" },
        { name, filters }
      );
      await queryClient.invalidateQueries({ queryKey: ["savedViews"] });
      sendNotification({
        type: "success",
        text: l10n.getString("transaction-export-save-view-success"),
      });
      return true;
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("transaction-export-save-view-error"),
      });
      return false;
    }
  };

  const updateView = async (): Promise<void> => {
    if (!selectedView) return;
    try {
      const filters = serializeFilters(currentFilters);
      await ApiHttp.fetch(
        `reports/saved_views/${selectedView.id}/`,
        { method: "PUT" },
        { name: selectedView.name, filters }
      );
      await queryClient.invalidateQueries({ queryKey: ["savedViews"] });
      sendNotification({
        type: "success",
        text: l10n.getString("transaction-export-update-view-success"),
      });
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("transaction-export-update-view-error"),
      });
    }
  };

  const validateName = (name: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return "Required";
    if (trimmed.length > MAX_VIEW_NAME_LENGTH) {
      return "Name must be 35 characters or fewer";
    }
    const isDuplicate = views.some(
      (v) =>
        v.view_type === "user" && v.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) return "A view with this name already exists";
    return null;
  };

  return {
    views,
    isLoading,
    selectedView,
    selectView,
    clearSelection,
    isModified,
    isPresetView,
    saveNewView,
    updateView,
    validateName,
  };
};
